import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";

const EXAMPLES = [
  "Announcement bar on karo: Diwali offer 40% off",
  "Pro plan ki fee 9999 karo aur popular banao",
  "Hero title change karo: Trade Smart, Get Funded",
  "Support email support@indifunded.in kar do",
];

export default function AdminAI() {
  const { reload } = useSettings();
  const [cmd, setCmd] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const run = async () => {
    if (!cmd.trim()) return;
    setLoading(true); setPreview(null);
    try { const { data } = await api.post("/admin/ai/command", { command: cmd }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI request failed"); }
    finally { setLoading(false); }
  };
  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/ai/apply", preview);
      toast.success(`Applied${data.plans_changed ? ` · ${data.plans_changed} plan(s) updated` : ""}`);
      setPreview(null); setCmd(""); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };
  const empty = preview && !Object.keys(preview.settings_patch || {}).length && !(preview.plan_updates || []).length;

  return (
    <div className="max-w-3xl">
      <div className="chip mb-3">Gemini AI</div>
      <h1 className="font-display text-4xl mb-2">AI Assistant</h1>
      <p className="text-[var(--muted)] mb-8">Type one command (English / Hindi / Gujarati) to change site content or plans. You will see a preview before anything is saved. For certificates, use “AI detect” in Certificates.</p>
      <div className="glass-card p-6">
        <textarea value={cmd} onChange={e => setCmd(e.target.value)} rows={4} className="input-dark resize-none" placeholder="e.g. Starter plan ka price 3999 karo" data-testid="ai-command-input"
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) run(); }}/>
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLES.map(x => <button key={x} onClick={() => setCmd(x)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--border)] text-[var(--muted)] hover:text-white">{x}</button>)}
        </div>
        <button onClick={run} disabled={loading || !cmd.trim()} className="btn-amber rounded-full px-5 py-2.5 text-sm mt-5 inline-flex items-center gap-2 disabled:opacity-60" data-testid="ai-command-run">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} {loading ? "Thinking…" : "Preview changes"}
        </button>
      </div>
      {preview && (
        <div className="glass-card p-6 mt-6" data-testid="ai-preview">
          <div className="text-xs uppercase font-mono text-[var(--muted-2)] mb-2">Summary</div>
          <p className="text-white mb-4">{preview.summary || "—"}</p>
          {!empty && <pre className="text-xs bg-black/40 rounded-xl p-4 overflow-auto max-h-96">{JSON.stringify({ settings: preview.settings_patch, plans: preview.plan_updates }, null, 2)}</pre>}
          <div className="flex gap-2 mt-5">
            {!empty && <button onClick={apply} disabled={applying} className="btn-amber rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="ai-apply"><Check size={14}/> {applying ? "Applying…" : "Apply changes"}</button>}
            <button onClick={() => setPreview(null)} className="btn-ghost rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"><X size={14}/> Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}
